import { BaseElement } from 'base';
import { bus } from 'bus';
import { cart } from 'cart';
import { amplifyBundles, isAmplifyOffer, pdpProperties } from 'amplify-bundles';
import { addItem, cartCount, cartExtras, refreshCart } from 'cart-extras';
import { product } from 'product';
import { productUrl } from 'product-url';
import { sectionFragment } from 'section-fragment';
import { SLIDE_CART_TOPIC, CART_DRAWER_ID } from 'slide-cart-topic';
import { PRODUCT_PURCHASE_TOPIC } from 'product-purchase-topic';
import { NOTIFY_MODAL_TOPIC } from 'notify-modal-topic';
import { BUNDLE_TIERS_TOPIC } from 'bundle-tiers-topic';
import { fillTemplate } from 'fill-template';

const VARIANT_TOPIC = 'product:variant';
const SELECTION_TOPIC = 'product:selection';
const TOP_INSET_TOPIC = 'viewport:top-inset';
const FRAGMENT = '[data-variant-fragment]';
const DELIVERY_NOTICE = 'inline-message[data-buy-box-delivery-notice]';
const DELIVERY_STATE = '[data-buy-box-delivery-restricted]';
const SLOT = '[data-buy-box-slot]';
const BUY_STATE = '[data-buy-box-state]';
const buyBoxState = state => `[data-buy-box-state="${state}"]`;
const PURCHASABLE = ['in-stock', 'pre-order'];
const ATC = PURCHASABLE.map(
  state => `${buyBoxState(state)} action-button`
).join(', ');
const stickyOffer = state => `template[data-sticky-offer="${state}"]`;
const stickyTierOffer = state => `template[data-sticky-offer-tier="${state}"]`;
const EXTRAS = '[data-cart-extras]';
const SYNCED_DATA = [
  'productId',
  'variantId',
  'url',
  'handle',
  'galleryCount',
  'stlIds',
  'colorIndex',
  'colorName',
  'optionNames',
  'amplifyPack',
  'priceSentinel',
  'compareSentinel'
];
const NOTIFY_STATE = buyBoxState('out-of-stock');
const VISIBLE_STATE = '[data-buy-box-state]:not([hidden])';
const FOCUSABLE = [
  "a[href]:not([tabindex='-1'])",
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
].join(', ');
const STYLE = 'link[rel="stylesheet"]';
const KEPT_STYLE = `:scope > ${STYLE}`;
const QUANTITY = '[data-buy-box-quantity]';

const isAbort = error => error?.name === 'AbortError';

const BUNDLE_REJECTION = /^The bundle product/;

const isBundleRejection = error =>
  error?.status === 422 && BUNDLE_REJECTION.test(error.message ?? '');

const PACK_RETRY_DELAY = 1500;
const PACK_DUPLICATE_WINDOW = 3500;
const PACK_READS = [0, 400, 1200];

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const isIntercepted = error => error?.code === 'intercepted';

class MainProductInfo extends BaseElement {
  #variants = [];
  #variant = null;
  #selection = [];
  #optionNames = [];
  #epoch = 0;
  #parsed = null;
  #navigating = false;
  #adding = false;
  #confirmed = { variant: null, selection: [] };
  #slid = false;
  #deferred = null;
  #selectionKey = null;
  #blocked = false;
  #packSlug = null;
  #packToken = 0;
  #packSentAt = 0;
  #ctaObserver = null;
  #ctaInView = null;
  #topInset = 0;
  #offerKey = null;
  #offerState = null;
  #tierPrice = null;
  #purchaseId = null;
  #notifyOpen = false;

  onConnect() {
    this.#epoch += 1;
    this.#parsed = null;
    this.#deferred = null;
    this.#ctaObserver = null;
    this.#ctaInView = null;
    this.#topInset = 0;
    this.#offerKey = null;
    this.#offerState = null;
    this.#tierPrice = null;
    this.#purchaseId = null;
    this.#notifyOpen = false;
    this.#setLocked(false);
    this.#setFragmentsBusy(false);

    this.cleanup(() => product.abort(this.#sectionId));
    this.subscribeLive(VARIANT_TOPIC, this.#onRerender);
    this.subscribeLive(NOTIFY_MODAL_TOPIC, this.#onNotifyActive);
    this.listen(window, 'pageshow', this.#onPageShow);
    this.cleanup(() => {
      this.#ctaObserver?.disconnect();
      this.#ctaObserver = null;
      this.#publishCta(null);
    });
    this.subscribe(TOP_INSET_TOPIC, this.#onTopInset);
    this.subscribeLive(PRODUCT_PURCHASE_TOPIC, this.#onPurchaseRequest);
    this.subscribe(BUNDLE_TIERS_TOPIC, this.#onTierSelection);
    this.#hydrate();
  }

  get #sectionId() {
    return this.dataset.sectionId;
  }

  #hydrate() {
    this.#variants = this.#readVariants();
    const id = Number(this.dataset.variantId);
    this.#variant = this.#variants.find(variant => variant.id === id) ?? null;
    this.#selection = this.#variant ? [...this.#variant.options] : [];
    this.#confirm();
    this.#optionNames = this.#readOptionNames();
    this.#retainStyles();

    this.#upgrade('color-picker', picker =>
      picker.run(change => this.#onColor(change))
    );
    this.#upgrade('option-picker', picker => {
      picker.run(change => this.#onOption(change));
      this.#reconcile(picker);
    });
    this.#bindCta();
    this.#packSlug = null;
    this.#blocked = this.dataset.amplifyPack === 'true';
    this.#applyPack();
    this.#resolvePack().catch(error => {
      console.error('[main-product] amplify pack check failed:', error);
    });
    this.#watchCta();
  }

  #onTopInset(value) {
    const inset = Math.max(0, Number(value) || 0);
    if (inset === this.#topInset) return;
    this.#topInset = inset;
    if (this.#ctaObserver) this.#watchCta();
  }

  #watchCta() {
    if (!this.dataset.ctaTopic) return;
    this.#ctaObserver?.disconnect();
    const states = new Map();
    const observer = new IntersectionObserver(
      entries => {
        if (observer !== this.#ctaObserver) return;
        for (const entry of entries)
          states.set(entry.target, entry.isIntersecting);
        this.#publishCta([...states.values()].some(Boolean));
      },
      { rootMargin: `${-this.#topInset}px 0px 0px 0px`, threshold: 0 }
    );
    this.#ctaObserver = observer;
    for (const state of this.qa(BUY_STATE)) observer.observe(state);
  }

  #publishCta(inView) {
    const topic = this.dataset.ctaTopic;
    if (!topic || inView === this.#ctaInView) return;
    this.#ctaInView = inView;
    bus.publish(topic, inView);
  }

  #onTierSelection(selection) {
    if (selection?.sectionId !== this.#sectionId) return;
    if (selection.productId !== Number(this.dataset.productId)) return;
    this.#tierPrice = selection.price
      ? {
          productId: selection.productId,
          price: selection.price,
          compareAtPrice: selection.compareAtPrice ?? ''
        }
      : null;
    if (this.#offerState) this.#publishOffer(this.#offerState);
  }

  #offerHtml(state) {
    const tier = this.#tierPrice;
    const { priceSentinel, compareSentinel } = this.dataset;
    const template =
      tier &&
      priceSentinel &&
      compareSentinel &&
      tier.productId === Number(this.dataset.productId) &&
      PURCHASABLE.includes(state)
        ? this.q(stickyTierOffer(state))
        : null;
    if (!template) return this.q(stickyOffer(state))?.innerHTML;
    return fillTemplate(template.innerHTML, {
      [priceSentinel]: tier.price,
      [compareSentinel]: tier.compareAtPrice
    });
  }

  #publishOffer(state) {
    const topic = this.dataset.offerTopic;
    if (!topic) return;
    const html = this.#offerHtml(state);
    if (!html) return;
    const key = `${state}\u0000${html}`;
    if (key === this.#offerKey) return;
    this.#offerKey = key;
    bus.publish(topic, {
      variantId: this.#variant?.id ?? null,
      sections: { [this.#sectionId]: html }
    });
  }

  async #onPurchaseRequest(request) {
    if (request?.status !== 'pending') return;
    if (request.sectionId !== this.#sectionId) return;
    if (request.id === this.#purchaseId) return;
    this.#purchaseId = request.id;
    bus.publish(PRODUCT_PURCHASE_TOPIC, { ...request, status: 'adding' });

    const busy = this.#adding || this.hasAttribute('aria-busy');
    const state = this.#availability(this.#variant);
    if (!busy && state === 'out-of-stock') {
      this.#openNotify();
      bus.publish(PRODUCT_PURCHASE_TOPIC, { ...request, status: 'done' });
      return;
    }
    if (busy || !PURCHASABLE.includes(state)) {
      bus.publish(PRODUCT_PURCHASE_TOPIC, { ...request, status: 'skipped' });
      return;
    }

    let status = 'done';
    try {
      await this.#addToCart();
    } catch (error) {
      console.error(
        '[main-product] purchase request add to cart failed:',
        error
      );
      status = 'failed';
    }
    if (this.isConnected)
      bus.publish(PRODUCT_PURCHASE_TOPIC, { ...request, status });
  }

  async #resolvePack() {
    const token = ++this.#packToken;
    if (this.dataset.amplifyPack !== 'true') return;
    await amplifyBundles.load(this.dataset.shop);
    if (!this.isConnected || token !== this.#packToken) return;
    const slug = amplifyBundles.autoPack(this.#variant?.id);
    if (!slug) return;
    this.#packSlug = slug;
    this.#blocked = false;
    this.#applyPack();
  }

  #applyPack() {
    this.#setState(this.#availability(this.#variant));
    this.#publishSelection();
  }

  #block() {
    this.#blocked = true;
    this.#packSlug = null;
    this.#setState('missing');
    this.#publishSelection();
  }

  #cartSource(provider) {
    return () =>
      (typeof provider.cartItems === 'function'
        ? Promise.resolve()
        : customElements.whenDefined(provider.localName)
      ).then(() => provider.cartItems(this.#selectionState()));
  }

  #selectionState() {
    const options = [...this.#selection];
    const colorIndex = parseInt(this.dataset.colorIndex, 10);
    const color =
      colorIndex >= 0
        ? (options[colorIndex] ?? null)
        : this.dataset.colorName || null;
    return {
      sectionId: this.#sectionId,
      productId: Number(this.dataset.productId),
      variantId: this.#variant?.id ?? null,
      available: this.#variant?.available === true && !this.#blocked,
      preOrder: this.#variant?.pre_order === true,
      availability: this.#availability(this.#variant),
      analytics: this.#variant?.analytics ?? null,
      options,
      color
    };
  }

  #publishSelection() {
    const selection = this.#selectionState();
    const key = `${selection.productId}:${selection.variantId}:${selection.available}:${selection.availability}:${selection.options.join('\u0000')}`;
    if (key === this.#selectionKey) return;
    this.#selectionKey = key;
    bus.publish(SELECTION_TOPIC, selection);
  }

  #reconcile(picker) {
    const { index, value } = picker;
    if (!value || !this.#selection.length) return;
    if (value === this.#selection[index]) return;

    this.#onOption({ index, value }).catch(error => {
      if (isAbort(error) || !picker.isConnected) return;
      console.error('[main-product] option sync failed:', error);
      picker.restore(this.#selection[index]);
    });
  }

  #onPageShow(event) {
    if (event.persisted && this.#navigating) location.reload();
  }

  #navigate(url) {
    this.#navigating = true;
    location.assign(url);
    return new DOMException('Navigating to the product page', 'AbortError');
  }

  #keptStyles() {
    return new Set(this.qa(KEPT_STYLE).map(link => link.getAttribute('href')));
  }

  #retainStyles() {
    const kept = this.#keptStyles();
    const clones = [];
    for (const link of this.qa(STYLE)) {
      const href = link.getAttribute('href');
      if (kept.has(href)) continue;
      kept.add(href);
      clones.push(link.cloneNode());
    }
    this.prepend(...clones);
  }

  #dropKnownStyles(root) {
    const kept = this.#keptStyles();
    for (const link of root.querySelectorAll(STYLE)) {
      if (kept.has(link.getAttribute('href'))) link.remove();
    }
  }

  #raiseError() {
    const text = this.q('.buy-box')?.dataset.error;
    if (text) bus.publish('toast:raised', { id: crypto.randomUUID(), text });
  }

  #readVariants() {
    const data = this.q('script[data-product-variants]')?.textContent;
    if (!data) return [];
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('[main-product] invalid variants data:', error);
      return [];
    }
  }

  #readOptionNames() {
    try {
      const names = JSON.parse(this.dataset.optionNames || '[]');
      return Array.isArray(names) ? names : [];
    } catch (error) {
      console.error('[main-product] invalid option names:', error);
      return [];
    }
  }

  #upgrade(selector, bind) {
    for (const element of this.qa(selector)) {
      customElements.whenDefined(element.localName).then(() => {
        if (element.isConnected) bind(element);
      });
    }
  }

  #bindCta() {
    this.#upgrade(ATC, button => {
      button.run(() => this.#addToCart());
      if (this.#adding) this.#setAtcLoading(true);
    });
    this.#upgrade(`${NOTIFY_STATE} action-button`, button => {
      button.run(() => this.#notify(button));
    });
  }

  #setAtcLoading(loading) {
    for (const button of this.qa(ATC))
      if (typeof button.setLoading === 'function') button.setLoading(loading);
  }

  #notify(button) {
    if (!this.#variant) return;

    this.#notifyOpen = true;
    const control = button.control;
    if (control && control !== document.activeElement)
      control.focus({ preventScroll: true, focusVisible: false });
    this.#openNotify();
  }

  #openNotify() {
    const variant = this.#variant;
    if (!variant) return;
    bus.publish(NOTIFY_MODAL_TOPIC, {
      productId: Number(this.dataset.productId),
      variantId: variant.id,
      handle: this.dataset.handle,
      from: 'product'
    });
  }

  #onNotifyActive(value) {
    if (value || !this.#notifyOpen) return;
    this.#notifyOpen = false;
    queueMicrotask(() => this.#restoreNotifyFocus());
  }

  #restoreNotifyFocus() {
    if (!this.isConnected) return;
    const active = document.activeElement;
    if (this.qa(VISIBLE_STATE).some(state => state.contains(active))) return;

    this.#ctaControl(this, NOTIFY_STATE)?.focus({ preventScroll: true });
  }

  #ctaControl(scope, preferred) {
    const enabled = state =>
      this.qa(`${state} [data-action-control]:not([disabled])`).find(control =>
        scope.contains(control)
      );
    return enabled(`${preferred}:not([hidden])`) ?? enabled(VISIBLE_STATE);
  }

  #focusables(fragment) {
    return this.qa(FOCUSABLE).filter(
      element =>
        fragment.contains(element) && element.getClientRects().length > 0
    );
  }

  #focusSlot(fragment, active) {
    return {
      state: active.closest('[data-buy-box-state]')?.dataset.buyBoxState,
      index: this.#focusables(fragment).indexOf(active)
    };
  }

  #refocus(fragment, { state, index }) {
    const focusables = this.#focusables(fragment);
    const control =
      (state ? this.#ctaControl(fragment, buyBoxState(state)) : null) ??
      focusables[index] ??
      focusables[0];
    control?.focus({ preventScroll: true });
  }

  async #addToCart() {
    const variant = this.#variant;
    if (this.#adding || !variant?.available || this.#blocked) return;

    const slug = this.#packSlug;
    const item = slug
      ? { id: variant.id, quantity: 1 }
      : {
          id: variant.id,
          quantity: this.#quantity(variant),
          properties: pdpProperties(variant.pre_order === true)
        };
    try {
      this.#adding = true;
      this.#setAtcLoading(true);
      this.#setPickersInert(true);
      await (slug ? this.#addPack(item, slug) : this.#addWithExtras(item));
    } catch (error) {
      if (this.isConnected) {
        if (!slug && isBundleRejection(error)) this.#block();
        this.#raiseError();
      }
      throw error;
    } finally {
      this.#adding = false;
      if (this.isConnected) {
        this.#setAtcLoading(false);
        this.#settleAdd();
      }
    }
    if (this.isConnected) bus.publish(SLIDE_CART_TOPIC, CART_DRAWER_ID);
  }

  #quantity(variant) {
    const min = Math.max(1, Number(variant.quantity_min) || 1);
    const step = Math.max(1, Number(variant.quantity_increment) || 1);
    const counter = this.q(QUANTITY);
    const owned = Number(counter?.dataset.variantId) === variant.id;
    const value = owned ? Number(counter.value) : NaN;
    const onGrid =
      Number.isInteger(value) && value >= min && (value - min) % step === 0;
    return onGrid ? value : min;
  }

  async #addWithExtras(item) {
    const providers = this.qa(EXTRAS);
    if (!providers.length) return addItem(item);

    const extras = await cartExtras.collect(
      providers.map(provider => this.#cartSource(provider))
    );
    return cartExtras.add(item, extras);
  }

  async #addPack(item, slug) {
    await this.#packWait(0);
    const before = await this.#packCount(slug);
    try {
      return await this.#sendPack(item);
    } catch (error) {
      if (error?.status !== 422 || before === null)
        return this.#failPack(error, slug, before, false);
    }

    await this.#packWait(PACK_RETRY_DELAY);
    const now = await this.#packCount(slug);
    if (now !== null && now > before) {
      await refreshCart();
      return null;
    }
    try {
      return await this.#sendPack(item);
    } catch (error) {
      return this.#failPack(error, slug, before, true);
    }
  }

  #packWait(minimum) {
    return delay(
      Math.max(minimum, this.#packSentAt + PACK_DUPLICATE_WINDOW - Date.now())
    );
  }

  #sendPack(item) {
    this.#packSentAt = Date.now();
    return cart.add({ items: [item], merge: false });
  }

  async #failPack(error, slug, before, retried) {
    const aborted = isAmplifyOffer(error);
    if (aborted || isIntercepted(error)) {
      if (await this.#packLanded(slug, before)) return null;
    }
    if (this.isConnected && (retried || (aborted && before !== null)))
      this.#block();
    throw error;
  }

  async #packLanded(slug, before) {
    const landed = before !== null && (await this.#waitForPack(slug, before));
    await refreshCart();
    return landed;
  }

  #packCount(slug) {
    const prefix = `cm${slug}`;
    return cartCount(line =>
      String(line.properties?._bundle ?? '').startsWith(prefix)
    );
  }

  async #waitForPack(slug, before) {
    let elapsed = 0;
    for (const at of PACK_READS) {
      await delay(at - elapsed);
      elapsed = at;
      const count = await this.#packCount(slug);
      if (count !== null && count > before) return true;
    }
    return false;
  }

  #settleAdd() {
    const deferred = this.#deferred;
    this.#deferred = null;
    if (deferred) {
      try {
        this.#applyFragments(deferred.html);
        this.#confirm(deferred.variant, deferred.selection);
      } catch (error) {
        console.error('[main-product] deferred render failed:', error);
      }
    }
    this.#setPickersInert(false);
  }

  #applyFragments(html) {
    this.#swapFragments(html);
    this.#syncDeliveryNotice();
    this.#bindCta();
    this.#applyPack();
    this.#watchCta();
  }

  #syncDeliveryNotice() {
    const restricted =
      this.q(DELIVERY_STATE)?.dataset.buyBoxDeliveryRestricted === 'true';
    this.#upgrade(DELIVERY_NOTICE, notice =>
      restricted ? notice.show() : notice.hide()
    );
  }

  #confirm(variant = this.#variant, selection = this.#selection) {
    this.#confirmed = { variant, selection: [...selection] };
    this.#slid = false;
  }

  async #onColor({ href, target }) {
    this.#epoch += 1;
    this.#deferred = null;
    const epoch = this.#epoch;
    const sectionId = this.#sectionId;
    const url = productUrl({
      href,
      search: location.search,
      origin: location.origin,
      variantId: this.#carryVariant(target)
    });

    if (this.#navigating) throw this.#navigate(url);
    this.#setLocked(true);

    let html = null;
    try {
      html = await product.render({ url, sectionId });
    } catch (error) {
      if (isAbort(error) || !this.isConnected || epoch !== this.#epoch) return;
      console.error('[main-product] color render failed:', error);
      throw this.#navigate(url);
    }
    if (!this.isConnected || epoch !== this.#epoch) return;

    const fresh = sectionFragment({ [sectionId]: html }, sectionId, this.id);
    if (!this.#canSwap(fresh)) throw this.#navigate(url);

    this.#parsed = { html, fresh };
    bus.publish(VARIANT_TOPIC, {
      productId: Number(fresh.dataset.productId),
      variantId: Number(fresh.dataset.variantId),
      sections: { [sectionId]: html },
      url
    });
    history.replaceState(history.state, '', url);
    this.#parsed = null;
  }

  #carryVariant(target) {
    if (!target) return null;

    const normalize = name => String(name).trim().toLowerCase();
    const ownColor = parseInt(this.dataset.colorIndex, 10);
    const ownNames = this.#optionNames.map(normalize);
    const constraints = [];
    target.names.forEach((name, index) => {
      if (index === target.colorIndex) return;
      const wanted = normalize(name);
      const position = ownNames.findIndex(
        (ownName, ownIndex) =>
          ownIndex !== ownColor &&
          ownName === wanted &&
          this.#selection[ownIndex] != null
      );
      if (position >= 0) constraints.push([index, this.#selection[position]]);
    });
    if (!constraints.length) return null;

    const candidates = target.variants.filter(variant =>
      constraints.every(([index, value]) => variant.options[index] === value)
    );
    return (
      (candidates.find(variant => variant.available) ?? candidates[0])?.id ??
      null
    );
  }

  #canSwap(fresh) {
    if (!fresh) return false;

    const hasMedia = Number(this.dataset.galleryCount) > 0;
    if (hasMedia !== Number(fresh.dataset.galleryCount) > 0) return false;
    if (this.dataset.stlIds !== fresh.dataset.stlIds) return false;

    return [fresh, ...fresh.querySelectorAll('*')].every(
      element =>
        !element.localName.includes('-') ||
        Boolean(customElements.get(element.localName))
    );
  }

  #onRerender(state) {
    const sectionId = this.#sectionId;
    const html = state?.sections?.[sectionId];
    if (!html) return;

    const parsed = this.#parsed;
    const fresh =
      parsed?.html === html
        ? parsed.fresh
        : sectionFragment(state.sections, sectionId, this.id);
    this.#parsed = null;
    if (!fresh) return;

    const active = this.getRootNode().activeElement;
    const focusId = this.contains(active) ? active.dataset?.productId : null;

    this.#deferred = null;
    this.#dropKnownStyles(fresh);
    const kept = this.qa(KEPT_STYLE);
    for (const node of [...this.childNodes]) {
      if (!kept.includes(node)) node.remove();
    }
    this.append(...fresh.childNodes);
    for (const key of SYNCED_DATA) this.dataset[key] = fresh.dataset[key] ?? '';
    this.#syncTitle(fresh);
    this.#setLocked(false);
    this.#hydrate();

    if (focusId)
      this.q(`[aria-current="true"][data-product-id="${focusId}"]`)?.focus({
        preventScroll: true
      });
  }

  async #onOption({ index, value }) {
    this.#epoch += 1;
    const epoch = this.#epoch;
    const sectionId = this.#sectionId;

    this.#deferred = null;
    this.#selection[index] = value;
    const variant = this.#match();
    this.#apply(variant);

    if (!variant) {
      product.abort(sectionId);
      this.#setFragmentsBusy(false);
      history.replaceState(history.state, '', this.#variantUrl(null));
      this.#confirm();
      return;
    }

    const url = this.#variantUrl(variant.id);
    history.replaceState(history.state, '', url);

    if (variant.media != null && this.#isColorOption(index)) {
      this.#publishMedia(variant);
      this.#slid = true;
    }

    this.#setFragmentsBusy(true);
    try {
      const html = await product.render({ url, sectionId });
      if (!this.isConnected || epoch !== this.#epoch) return;
      if (this.#adding) {
        this.#deferred = {
          html,
          variant: this.#variant,
          selection: [...this.#selection]
        };
        return;
      }
      this.#applyFragments(html);
      this.#confirm();
    } catch (error) {
      if (isAbort(error) || !this.isConnected || epoch !== this.#epoch) return;
      this.#restoreConfirmed();
      this.#raiseError();
      throw error;
    } finally {
      if (this.isConnected && epoch === this.#epoch)
        this.#setFragmentsBusy(false);
    }
  }

  #restoreConfirmed() {
    const { variant, selection } = this.#confirmed;
    this.#selection = [...selection];
    for (const picker of this.qa('option-picker')) {
      if (typeof picker.restore !== 'function') continue;
      picker.restore(this.#selection[picker.index]);
    }
    this.#apply(variant);
    history.replaceState(
      history.state,
      '',
      this.#variantUrl(variant?.id ?? null)
    );
    if (this.#slid && variant?.media != null) this.#publishMedia(variant);
    this.#slid = false;
  }

  #variantUrl(variantId) {
    return productUrl({
      href: this.dataset.url,
      search: location.search,
      origin: location.origin,
      variantId
    });
  }

  #publishMedia(variant) {
    bus.publish(VARIANT_TOPIC, {
      productId: Number(this.dataset.productId),
      variantId: variant.id,
      mediaId: variant.media
    });
  }

  #match() {
    return (
      this.#variants.find(variant =>
        variant.options.every(
          (option, position) => option === this.#selection[position]
        )
      ) ?? null
    );
  }

  #apply(variant) {
    this.#variant = variant;
    this.dataset.variantId = variant ? String(variant.id) : '';
    this.#syncPickers();
    this.#setState(this.#availability(variant));
    this.#publishSelection();
  }

  #syncPickers() {
    for (const picker of this.qa('option-picker')) {
      if (typeof picker.sync !== 'function') continue;
      picker.sync({ states: this.#statesFor(picker.index) });
    }
  }

  #statesFor(index) {
    const aggregate = this.#isColorOption(index);
    const states = {};
    for (const variant of this.#variants) {
      const option = variant.options[index];
      states[option] ??= 'missing';
      if (states[option] === 'available') continue;
      const counts =
        aggregate ||
        variant.options.every(
          (value, position) =>
            position === index || value === this.#selection[position]
        );
      if (counts) states[option] = variant.available ? 'available' : 'sold-out';
    }
    return states;
  }

  #isColorOption(index) {
    return (
      this.qa('option-picker')
        .find(element => Number(element.dataset.optionIndex) === index)
        ?.hasAttribute('data-color-option') ?? false
    );
  }

  #swapFragments(html) {
    const sectionId = this.#sectionId;
    const fresh = sectionFragment({ [sectionId]: html }, sectionId, this.id);
    if (!fresh) throw new Error('Product section missing in render response');
    this.#dropKnownStyles(fresh);

    const incoming = new Map(
      Array.from(fresh.querySelectorAll(FRAGMENT), element => [
        element.id,
        element
      ])
    );
    const active = document.activeElement;
    for (const local of this.qa(FRAGMENT)) {
      const next = incoming.get(local.id);
      if (!next) continue;
      const slot = local.contains(active)
        ? this.#focusSlot(local, active)
        : null;
      local.replaceWith(next);
      if (!slot) continue;
      this.#setState(this.#availability(this.#variant));
      this.#refocus(next, slot);
    }
    this.#syncTitle(fresh);
  }

  #syncTitle(fresh) {
    const title = fresh.dataset.documentTitle;
    if (title) document.title = title;
  }

  #availability(variant) {
    if (!variant || this.#blocked) return 'missing';
    if (!variant.available) return 'out-of-stock';
    if (variant.pre_order_cta) return 'pre-order';
    return 'in-stock';
  }

  #setState(state) {
    this.#offerState = state;
    for (const container of this.qa(BUY_STATE))
      container.hidden = container.dataset.buyBoxState !== state;
    this.#publishOffer(state);
  }

  #setLocked(locked) {
    if (locked) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    for (const element of this.qa(`option-picker, ${FRAGMENT}, ${SLOT}`))
      element.inert = locked;
    if (!locked) this.#setPickersInert(false);
  }

  #setPickersInert(inert) {
    const locked = inert || this.#adding || this.hasAttribute('aria-busy');
    for (const picker of this.qa('option-picker')) picker.inert = locked;
    for (const picker of this.qa('color-picker')) picker.inert = this.#adding;
  }

  #setFragmentsBusy(busy) {
    for (const fragment of this.qa(FRAGMENT)) {
      if (busy) fragment.setAttribute('aria-busy', 'true');
      else fragment.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('main-product-info'))
  customElements.define('main-product-info', MainProductInfo);
